import { useMemo } from 'react';

import { ApiQueryBasePromise } from '@/types';
import { useSiteStore } from '@/store/site';
import { getAuthToken } from '@/store/auth';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { getResponseJwtHeader } from '@/utils/web';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { getFormData } from '@nf/utils-common/form';
import { siteService } from '@/services/site.service';
import { LicenseeTypeEnums } from '@nf/types/site';
import { useGetSiteConfigs } from './use-site-configs';
import { useApiBackendInfo } from './use-api-backend-info';

import type { ApiBackendInfo } from './athena/types';

interface BannerInfo {
	data: string,
	status: number
}

/**
 * CMS banner item. Keeps the legacy GetBannerInfo field names so both sources share one shape.
 */
export type BannerRawItem = {
	KeyId: string;
	Img: string;
	enable?: string;
	navigateKey?: string;
	actType?: string;
	/** Img is already a full URL (imageUrlV2): skip the image server path */
	imgIsFinal?: boolean;
};

/** Only the fields galaxy needs; startDate/endDate/loading are unused */
type CmsBannerResponseItem = {
	imageUrl: string;
	imageUrlV2: string | null;
	link: string;
	m_Enable: string;
	actType: string;
};

type CmsBannerResponse = {
	ErrorCode: number;
	ErrorMessage: string;
	Data: CmsBannerResponseItem[] | null;
};

/** Always request the after banner, same as legacy GetBannerInfo */
const IS_BEFORE_BANNER = false;

const fetchBannerInfo = async (): Promise<ApiQueryBasePromise<BannerInfo>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(`${apiDomain}/Default/GetBannerInfo`), { body: getFormData({ isBeforeBanner: IS_BEFORE_BANNER }) })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const parseLegacyBannerItems = (raw?: string): BannerRawItem[] => {
	try {
		const items = JSON.parse(raw ?? '');
		return Array.isArray(items) ? items : [];
	} catch {
		return [];
	}
};

export const mapCmsBannerItem = (item: CmsBannerResponseItem): BannerRawItem => ({
	KeyId: item.imageUrl,
	Img: item.imageUrlV2 || item.imageUrl,
	imgIsFinal: Boolean(item.imageUrlV2),
	enable: item.m_Enable,
	navigateKey: item.link,
	actType: item.actType
});

export const fetchCmsBanner = async (apiBackendInfo: ApiBackendInfo, isAthena: boolean): Promise<BannerRawItem[]> => {
	const apiUrl = `${apiBackendInfo.url}/Banner/GetCmsBanner?isBeforeBanner=${IS_BEFORE_BANNER}&isAthena=${isAthena}`;
	const response = await fetchComposer.getWithBearer(cloudUrlParser(apiUrl))(apiBackendInfo.token);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	const result: CmsBannerResponse = await response.json();
	if (result.ErrorCode !== 0) {
		throw new Error(`${result.ErrorCode}: ${result.ErrorMessage}`);
	}

	return Array.isArray(result.Data) ? result.Data.map(mapCmsBannerItem) : [];
};

export const useBannerInfo = () => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const apiBackendInfo = useApiBackendInfo();
	const isAthena = siteService.getLicenseeType() === LicenseeTypeEnums.Athena;
	const useApiBackend = Boolean(SiteConfigsData?.EnableBannerInfoApiBackend);
	const isGetBannerInfoReady = SiteConfigsData != null;
	const isGetCmsBannerReady = apiBackendInfo.isReady;

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.GET_BANNER_INFO, 'BannerInfo', useApiBackend ? 'apibackend' : 'ctcd'],
		queryFn: () => useApiBackend
			? fetchCmsBanner(apiBackendInfo, isAthena)
			: fetchBannerInfo().then(response => parseLegacyBannerItems(response.data.data)),
		cacheTime: 1000 * 60 * 3,
		refetchOnWindowFocus: false,
		enabled: useApiBackend ? isGetCmsBannerReady : isGetBannerInfoReady
	});

	const bannerItems = useMemo(() => data ?? [], [data]);

	return {
		bannerItems,
		isLoading,
		isError,
		error,
		refetch
	};
};
